import { Button, Card, Input } from "@omnichannel/ui";
import { PersonDirectory } from "./PersonDirectory";
import { useEffect, useRef, useState } from "react";
import { RevenueSnapshot } from "./RevenueSnapshot";
import { SupportConversations } from "./SupportConversations";
import { Tariffs } from "./Tariffs";
import { Trials } from "./Trials";
import { AppUpdates } from "./AppUpdates";
import { Monitoring } from "./Monitoring";
import { ApiError, getToken, request, setToken } from "./api";
import type {
  AnalyticsSummary,
  AppCreatedRow,
  AppRow,
  CustomerRow,
} from "./api";

function AuthScreen({ onAuthenticated, notice }: { onAuthenticated: () => void; notice?: string | null }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setPending(true);
    try {
      const path =
        mode === "login" ? "/api/v1/auth/login" : "/api/v1/auth/register";
      const res = await request<{ access_token: string }>(path, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setToken(res.access_token);
      onAuthenticated();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось выполнить запрос",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth-screen">
      <Card className="auth-card">
        <h1>{mode === "login" ? "Вход в Fleet Hub" : "Регистрация"}</h1>
        {notice && <p className="error-text" role="alert">{notice}</p>}
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="fh-email">Email</label>
            <Input
              id="fh-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="fh-password">Пароль</label>
            <Input
              id="fh-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          {error && <p className="error-text">{error}</p>}
          <Button className="primary" type="submit" disabled={pending}>
            {mode === "login" ? "Войти" : "Создать аккаунт"}
          </Button>
        </form>
        <Button
          variant="ghost"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
        >
          {mode === "login"
            ? "Ещё нет аккаунта? Зарегистрироваться"
            : "Уже есть аккаунт? Войти"}
        </Button>
      </Card>
    </div>
  );
}

function CreateAppForm({
  onCreated,
}: {
  onCreated: (app: AppCreatedRow) => void;
}) {
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setPending(true);
    setError("");
    try {
      const app = await request<AppCreatedRow>("/api/v1/apps", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      setName("");
      onCreated(app);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось создать приложение",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{ display: "flex", gap: 8, marginBottom: 24 }}
    >
      <Input
        placeholder="Название нового приложения"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button
        className="primary"
        style={{ width: "auto", padding: "10px 20px" }}
        disabled={pending}
        type="submit"
      >
        Добавить приложение
      </Button>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}

function ApiKeyReveal({
  app,
  onDismiss,
}: {
  app: AppCreatedRow;
  onDismiss: () => void;
}) {
  return (
    <div className="api-key-reveal">
      <strong>
        API-ключ для «{app.name}» — скопируйте сейчас, больше он не будет
        показан:
      </strong>
      <code>{app.api_key}</code>
      <div style={{ marginTop: 12 }}>
        <Button variant="secondary" onClick={onDismiss}>
          Я скопировал(а) ключ
        </Button>
      </div>
    </div>
  );
}

function AppsList({ onOpenApp }: { onOpenApp: (appId: string) => void }) {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [revealedApp, setRevealedApp] = useState<AppCreatedRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError("");
    request<AppRow[]>("/api/v1/apps")
      .then(setApps)
      .catch((err) =>
        setLoadError(
          err instanceof ApiError
            ? err.message
            : "Не удалось загрузить приложения",
        ),
      )
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="shell">
      <div className="shell-header">
        <h1>Приложения</h1>
      </div>
      {revealedApp && (
        <ApiKeyReveal
          app={revealedApp}
          onDismiss={() => {
            setRevealedApp(null);
            load();
          }}
        />
      )}
      <CreateAppForm onCreated={setRevealedApp} />
      {loadError && (
        <p role="alert">
          {loadError} <Button onClick={load}>Повторить</Button>
        </p>
      )}
      {loading && <p role="status">Загрузка…</p>}
      {!loading && apps.length === 0 && (
        <p>Пока нет ни одного приложения — добавьте первое выше.</p>
      )}
      {apps.map((app) => (
        <Button
          variant="secondary"
          className="app-card"
          key={app.id}
          onClick={() => onOpenApp(app.id)}
        >
          <div>
            <div>{app.name}</div>
            <div className="meta">
              /{app.slug} · ключ ····{app.api_key_last4}
            </div>
          </div>
          <span className="meta">
            {app.is_active ? "активно" : "отключено"}
          </span>
        </Button>
      ))}
    </div>
  );
}

function AppDetail({ appId, onBack }: { appId: string; onBack: () => void }) {
  const [app, setApp] = useState<AppRow | null>(null);
  const [summaryState, setSummary] = useState<{ appId: string; value: AnalyticsSummary } | null>(null);
  const [summaryPending, setSummaryPending] = useState(true);
  const generation = useRef(0);
  const summary = summaryState?.appId === appId ? summaryState.value : null;
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionPending, setActionPending] = useState(false);
  const [showTariffs, setShowTariffs] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const load = () => {
    const current = ++generation.current;
    setSummary(null);
    setSummaryPending(true);
    request<AppRow>(`/api/v1/apps/${appId}`)
      .then((value) => { if (current === generation.current) setApp(value); })
      .catch((err) => {
        if (current === generation.current) setActionError(
          err instanceof ApiError ? err.message : "Не удалось загрузить приложение",
        );
      });
    request<AnalyticsSummary>(`/api/v1/apps/${appId}/analytics/summary`)
      .then((value) => {
        if (current === generation.current) setSummary({ appId, value });
      })
      .catch((err) => {
        if (current === generation.current) setActionError(
          err instanceof ApiError ? err.message : "Не удалось загрузить аналитику",
        );
      })
      .finally(() => { if (current === generation.current) setSummaryPending(false); });
    request<CustomerRow[]>(`/api/v1/apps/${appId}/customers`)
      .then((value) => { if (current === generation.current) setCustomers(value); })
      .catch((err) => {
        if (current === generation.current) setActionError(
          err instanceof ApiError ? err.message : "Не удалось загрузить клиентов",
        );
      });
  };

  useEffect(() => {
    load();
    return () => { generation.current++; };
  }, [appId]);

  const handleRegenerateKey = async () => {
    if (!confirm("Старый ключ сразу перестанет работать. Продолжить?")) return;
    setActionError("");
    setActionPending(true);
    try {
      const res = await request<{ api_key: string }>(
        `/api/v1/apps/${appId}/regenerate-key`,
        { method: "POST" },
      );
      setNewKey(res.api_key);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось перевыпустить ключ",
      );
    } finally {
      setActionPending(false);
    }
  };

  const handleToggleActive = async () => {
    if (!app) return;
    setActionError("");
    setActionPending(true);
    try {
      const updated = await request<AppRow>(`/api/v1/apps/${appId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !app.is_active }),
      });
      setApp(updated);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Не удалось изменить статус",
      );
    } finally {
      setActionPending(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Удалить приложение «${app?.name}»? Это необратимо.`)) return;
    setActionError("");
    setActionPending(true);
    try {
      await request(`/api/v1/apps/${appId}`, { method: "DELETE" });
      onBack();
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.status === 409
            ? "У приложения уже есть события — удалить нельзя, можно только отключить."
            : err.message
          : "Не удалось удалить приложение",
      );
    } finally {
      setActionPending(false);
    }
  };

  return (
    <div className="shell">
      <Button variant="ghost" className="back-link" onClick={onBack}>
        ← Все приложения
      </Button>
      <div className="shell-header">
        <h1>{app?.name || "…"}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            disabled={actionPending}
            onClick={handleRegenerateKey}
          >
            Перевыпустить ключ
          </Button>
          <Button
            variant="secondary"
            disabled={actionPending}
            onClick={handleToggleActive}
          >
            {app?.is_active ? "Отключить" : "Включить"}
          </Button>
          <Button
            variant="secondary"
            disabled={actionPending}
            onClick={handleDelete}
          >
            Удалить
          </Button>
        </div>
      </div>
      {newKey && (
        <div className="api-key-reveal">
          <strong>Новый ключ — скопируйте сейчас, больше не покажем:</strong>
          <code>{newKey}</code>
          <div style={{ marginTop: 12 }}>
            <Button
              variant="secondary"
              onClick={() => {
                setNewKey(null);
                load();
              }}
            >
              Я скопировал(а) ключ
            </Button>
          </div>
        </div>
      )}
      {actionError && (
        <p className="error-text" role="alert">
          {actionError}
        </p>
      )}
      {app && !app.is_active && (
        <p className="error-text">
          Приложение отключено — новые события не принимаются.
        </p>
      )}
      {summary && (
        <div className="metrics">
          <Card className="metric-card">
            <div className="value">{summary.active_total}</div>
            <div className="label">Активных подписок</div>
          </Card>
          <Card className="metric-card">
            <div className="value">{summary.not_renewed_count}</div>
            <div className="label">Не продлили</div>
          </Card>
          <Card className="metric-card">
            <div className="value">{summary.churned_count}</div>
            <div className="label">Отменили</div>
          </Card>

        </div>
      )}
      <RevenueSnapshot snapshot={summary?.revenue_snapshot} loading={summaryPending} />
      <table>
        <thead>
          <tr>
            <th>Клиент</th>
            <th>Последнее событие</th>
            <th>Тариф</th>
            <th>Продление</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((c) => (
            <tr key={c.id}>
              <td>{c.external_customer_id}</td>
              <td>{c.last_event_type || "—"}</td>
              <td>{c.tariff || "—"}</td>
              <td>
                {c.renews_at
                  ? new Date(c.renews_at).toLocaleDateString("ru-RU")
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {customers.length === 0 && <p>Пока нет ни одного клиента.</p>}
      <section aria-label="Тарифы приложения" style={{ marginTop: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2>Тарифы</h2>
          <Button
            variant="secondary"
            aria-expanded={showTariffs}
            onClick={() => setShowTariffs((prev) => !prev)}
          >
            {showTariffs ? "Скрыть тарифы" : "Тарифы"}
          </Button>
        </div>
        {showTariffs && <Tariffs key={appId} appId={appId} />}
      </section>
      <section aria-label="Поддержка приложения" style={{ marginTop: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2>Поддержка</h2>
          <Button
            variant="secondary"
            aria-expanded={showSupport}
            onClick={() => setShowSupport((prev) => !prev)}
          >
            {showSupport ? "Скрыть поддержку" : "Поддержка"}
          </Button>
        </div>
        {showSupport && <SupportConversations key={appId} appId={appId} />}
      </section>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(() => !!getToken());
  const [view, setView] = useState<"apps" | "persons" | "trials" | "updates" | "monitoring">("apps");
  const [openAppId, setOpenAppId] = useState<string | null>(null);
  const [authStorageError, setAuthStorageError] = useState<string | null>(null);
  const logoutWarning = "Выход не сохранён. Очистите данные этого сайта в браузере перед закрытием вкладки.";
  const logout = () => {
    try {
      setToken(null);
      setAuthStorageError(null);
    } catch {
      setAuthStorageError(logoutWarning);
    }
    setAuthenticated(false);
    setOpenAppId(null);
    setView("apps");
  };


  useEffect(() => {
    const authContextChanged = () => {
      // A different tab changed the shared bearer. Return this tab to the
      // login screen and discard its owner-scoped UI state without overwriting
      // the new tab's credentials.
      setAuthenticated(false);
      setOpenAppId(null);
      setView("apps");
    };
    const storageFailure = () => {
      authContextChanged();
      setAuthStorageError(logoutWarning);
    };
    window.addEventListener("fleet-hub-auth-storage-error", storageFailure);
    window.addEventListener("fleet-hub-unauthorized", logout);
    window.addEventListener("fleet-hub-auth-context-changed", authContextChanged);
    return () => {
      window.removeEventListener("fleet-hub-auth-storage-error", storageFailure);
      window.removeEventListener("fleet-hub-unauthorized", logout);
      window.removeEventListener("fleet-hub-auth-context-changed", authContextChanged);
    };
  }, []);

  if (!authenticated) {
    return <AuthScreen notice={authStorageError} onAuthenticated={() => { setAuthStorageError(null); setAuthenticated(true); }} />;
  }

  if (openAppId) {
    return <AppDetail key={openAppId} appId={openAppId} onBack={() => setOpenAppId(null)} />;
  }

  return (
    <>
      <nav className="admin-nav" aria-label="Разделы">
        <Button variant="ghost" onClick={() => setView("apps")}>
          Продукты
        </Button>
        <Button variant="ghost" onClick={() => setView("persons")}>
          Клиенты и биллинг
        </Button>
        <Button variant="ghost" onClick={() => setView("trials")}>
          Пробные установки
        </Button>
        <Button variant="ghost" onClick={() => setView("updates")}>
          Обновления приложений
        </Button>
        <Button variant="ghost" onClick={() => setView("monitoring")}>
          Мониторинг
        </Button>
        <Button
          variant="ghost"
          onClick={logout}
        >
          Выйти
        </Button>
      </nav>
{view === "persons" ? <PersonDirectory /> : view === "trials" ? <Trials /> : view === "updates" ? <AppUpdates /> : view === "monitoring" ? <Monitoring /> : <AppsList onOpenApp={setOpenAppId} />}
    </>
  );
}
